import React, { useEffect, useState } from "react"

import { trpc } from "../../lib/trpc"

interface Newsletter {
  id: string
  title: string
  selectedSources: string[]
  dateRange?: { from: Date; to: Date }
  aggregatedContent?: any[]
}

interface Source {
  id: string
  name: string
  type: string
  tableName: string
  isActive: boolean
}

interface ContentAggregatorProps {
  newsletter: Newsletter
  onNewsletterChange: (updates: Partial<Newsletter>) => void
}

export default function ContentAggregator({ newsletter, onNewsletterChange }: ContentAggregatorProps) {
  const [selectedSources, setSelectedSources] = useState<string[]>(newsletter.selectedSources || [])
  const [dateRange, setDateRange] = useState({
    from: newsletter.dateRange?.from ? new Date(newsletter.dateRange.from).toISOString().split("T")[0] : "",
    to: newsletter.dateRange?.to ? new Date(newsletter.dateRange.to).toISOString().split("T")[0] : "",
  })
  const [aggregationFilters, setAggregationFilters] = useState({
    keywords: [] as string[],
    excludeKeywords: [] as string[],
    minEngagement: 0,
  })

  // Fetch available sources
  const { data: sources, isLoading: sourcesLoading } = trpc.newsletter.sources.list.useQuery()

  // Aggregate content mutation
  const aggregateContent = trpc.newsletter.aggregateContent.useMutation({
    onSuccess: (result) => {
      // Refresh the newsletter data to get the aggregated content
      void refetchAggregatedContent()
      alert(
        `Content aggregated successfully! Found ${result.contentSummary.totalItems} items from ${result.contentSummary.totalSources} sources.`,
      )
    },
    onError: (error) => {
      alert(`Failed to aggregate content: ${error.message}`)
    },
  })

  // Get aggregated content
  const {
    data: aggregatedContentData,
    refetch: refetchAggregatedContent,
    isLoading: contentLoading,
  } = trpc.newsletter.getAggregatedContent.useQuery(newsletter.id)

  // Get newsletter stats
  const { data: stats } = trpc.newsletter.getStats.useQuery(newsletter.id)

  const handleSourceToggle = (sourceId: string) => {
    const newSelectedSources = selectedSources.includes(sourceId)
      ? selectedSources.filter((id) => id !== sourceId)
      : [...selectedSources, sourceId]

    setSelectedSources(newSelectedSources)
    onNewsletterChange({ selectedSources: newSelectedSources })
  }

  const handleDateRangeChange = (field: "from" | "to", value: string) => {
    const newDateRange = { ...dateRange, [field]: value }
    setDateRange(newDateRange)

    const dateRangeObj = {
      from: newDateRange.from ? new Date(newDateRange.from) : undefined,
      to: newDateRange.to ? new Date(newDateRange.to) : undefined,
    }

    onNewsletterChange({ dateRange: dateRangeObj })
  }

  const handleAggregateContent = () => {
    if (selectedSources.length === 0) {
      alert("Please select at least one content source.")
      return
    }

    const filters = {
      keywords: aggregationFilters.keywords.filter((k) => k.trim()),
      excludeKeywords: aggregationFilters.excludeKeywords.filter((k) => k.trim()),
      minEngagement: aggregationFilters.minEngagement || undefined,
      dateRange:
        dateRange.from && dateRange.to
          ? {
              from: new Date(dateRange.from),
              to: new Date(dateRange.to),
            }
          : undefined,
    }

    aggregateContent.mutate({
      newsletterId: newsletter.id,
      sources: selectedSources,
      filters,
    })
  }

  const addKeyword = (type: "keywords" | "excludeKeywords") => {
    const keyword = prompt(`Enter ${type === "keywords" ? "keyword to include" : "keyword to exclude"}:`)
    if (keyword) {
      setAggregationFilters((prev) => ({
        ...prev,
        [type]: [...prev[type], keyword.trim()],
      }))
    }
  }

  const removeKeyword = (type: "keywords" | "excludeKeywords", index: number) => {
    setAggregationFilters((prev) => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index),
    }))
  }

  const sourcesByType =
    sources?.reduce(
      (acc, source) => {
        if (!acc[source.type]) acc[source.type] = []
        acc[source.type].push(source)
        return acc
      },
      {} as Record<string, Source[]>,
    ) || {}

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      {stats && (
        <div className="bg-background rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold  mb-4">Content Overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold ">{stats.totalSources}</div>
              <div className="text-sm text-gray-500">Sources</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{stats.totalItems}</div>
              <div className="text-sm text-gray-500">Total Items</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{stats.includedItems}</div>
              <div className="text-sm text-gray-500">Included</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-destructive">{stats.sourcesWithErrors}</div>
              <div className="text-sm text-gray-500">Errors</div>
            </div>
          </div>
          {stats.lastAggregated && (
            <p className="mt-4 text-sm text-gray-500">
              Last aggregated: {new Date(stats.lastAggregated).toLocaleString()}
            </p>
          )}
        </div>
      )}

      {/* Source Selection */}
      <div className="bg-background rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold  mb-4">Content Sources</h2>

        {sourcesLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 ">Loading sources...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(sourcesByType).map(([type, typeSources]) => (
              <div key={type} className="border rounded-lg p-4">
                <h3 className="font-medium  mb-3 capitalize">{type.replace("_", " ")} Sources</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {typeSources.map((source) => (
                    <label
                      key={source.id}
                      className="flex items-center space-x-3 p-3 border rounded-lg  cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSources.includes(source.id)}
                        onChange={() => handleSourceToggle(source.id)}
                        className="h-4 w-4  focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium  truncate">{source.name}</p>
                        <p className="text-xs text-gray-500">Table: {source.tableName}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))}

            {Object.keys(sourcesByType).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No content sources available. Please configure sources first.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Date Range & Filters */}
      <div className="bg-background rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold  mb-4">Filters & Date Range</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Date Range */}
          <div>
            <h3 className="text-lg font-medium  mb-3">Date Range</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-primary">From</label>
                <input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => handleDateRangeChange("from", e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary">To</label>
                <input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => handleDateRangeChange("to", e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>
          </div>

          {/* Keyword Filters */}
          <div>
            <h3 className="text-lg font-medium  mb-3">Content Filters</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-2">Include Keywords</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {aggregationFilters.keywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full"
                    >
                      {keyword}
                      <button
                        onClick={() => removeKeyword("keywords", index)}
                        className="ml-1 text-green-600 hover:text-green-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <button onClick={() => addKeyword("keywords")} className="text-sm  hover:text-blue-800">
                  + Add keyword
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2">Exclude Keywords</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {aggregationFilters.excludeKeywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-destructive rounded-full"
                    >
                      {keyword}
                      <button
                        onClick={() => removeKeyword("excludeKeywords", index)}
                        className="ml-1 text-destructive hover:text-destructive"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <button onClick={() => addKeyword("excludeKeywords")} className="text-sm  hover:text-blue-800">
                  + Add keyword to exclude
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary">Minimum Engagement</label>
                <input
                  type="number"
                  min="0"
                  value={aggregationFilters.minEngagement}
                  onChange={(e) =>
                    setAggregationFilters((prev) => ({
                      ...prev,
                      minEngagement: parseInt(e.target.value) || 0,
                    }))
                  }
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t">
          <button
            onClick={handleAggregateContent}
            disabled={aggregateContent.isLoading || selectedSources.length === 0}
            className="w-full md:w-auto inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {aggregateContent.isLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                Aggregating Content...
              </>
            ) : (
              <>
                <svg className="-ml-1 mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Aggregate Content
              </>
            )}
          </button>
        </div>
      </div>

      {/* Aggregated Content Preview */}
      {aggregatedContentData?.aggregatedContent && (
        <div className="bg-background rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold  mb-4">Aggregated Content</h2>

          {contentLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 ">Loading content...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {aggregatedContentData.aggregatedContent.map((section: any, index: number) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-medium ">{section.sourceName}</h3>
                    <span className="text-sm text-gray-500">{section.items?.length || 0} items</span>
                  </div>

                  {section.error ? (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                      <p className="text-sm text-destructive">{section.error}</p>
                    </div>
                  ) : (
                    <>
                      {section.summary && (
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-3">
                          <p className="text-sm text-blue-800">{section.summary}</p>
                        </div>
                      )}

                      {section.items && section.items.length > 0 && (
                        <div className="space-y-2">
                          {section.items.slice(0, 3).map((item: any, itemIndex: number) => (
                            <div key={itemIndex} className="text-sm  border-l-2 border-border pl-3">
                              <p className="font-medium">{item.title}</p>
                              {item.summary && <p className="text-gray-500 mt-1">{item.summary}</p>}
                            </div>
                          ))}
                          {section.items.length > 3 && (
                            <p className="text-sm text-gray-500 italic">
                              ... and {section.items.length - 3} more items
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
